import React from 'react';
import { CheckCircle2, CreditCard } from 'lucide-react';

const MODULE_BY_VENDOR = {
  clinic: 'clinic_cms',
  doctor: 'doctor_cms',
  pharmacy: 'pharmacy_cms',
  lab: 'lab_cms',
  homecare: 'homecare_cms',
};

const parseModules = (value) => {
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value || '[]'); } catch (_error) { return []; }
};

export default function PlanAssignmentPanel({ vendorType, plans = [], value = {}, onChange, editMode = false }) {
  const moduleKey = MODULE_BY_VENDOR[vendorType];
  const applicablePlans = plans.filter((plan) => Number(plan.is_active) === 1 && parseModules(plan.included_modules).includes(moduleKey));
  const selected = applicablePlans.find((plan) => String(plan.id) === String(value.plan_id));

  const selectPlan = (planId) => {
    const plan = applicablePlans.find((item) => String(item.id) === String(planId));
    if (!plan) return onChange({ ...value, plan_id: '' });
    const start = value.billing_start_date || new Date().toISOString().slice(0, 10);
    const planModules = parseModules(plan.included_modules);
    const enabledModules = planModules.filter((key) => key === moduleKey || key === 'lead_generation');
    const hasLead = enabledModules.includes('lead_generation');
    const hasCms = enabledModules.some((key) => key !== 'lead_generation');
    const end = new Date(`${start}T00:00:00`);
    if (['yearly', 'annual'].includes(plan.billing_cycle || 'monthly')) end.setFullYear(end.getFullYear() + 1);
    else end.setMonth(end.getMonth() + 1);
    onChange({
      ...value,
      plan_id: plan.id,
      commercial_model: plan.commercial_model || 'subscription_only',
      business_model: hasLead && hasCms ? 'hybrid' : hasLead ? 'leads_only' : 'cms_only',
      enabled_modules: enabledModules,
      subscription_amount: plan.commercial_model === 'commission_only' ? 0 : Number(plan.base_price || 0),
      currency: plan.currency || 'INR',
      commission_rate: Number(plan.commission_rate || 0),
      commission_type: plan.commission_type || 'percentage',
      billing_start_date: start,
      billing_end_date: end.toISOString().slice(0, 10),
      payment_status: value.payment_status || 'pending',
      subscription_status: value.subscription_status || 'active',
    });
  };

  const formatDate = (date) => date ? String(date).slice(0, 10) : '-';

  return (
    <section className="plan-assignment-panel">
      <div className="plan-assignment-heading">
        <span><CreditCard size={18} /></span>
        <div><h3>Subscription Plan &amp; Pricing</h3><p>Assign the commercial plan that controls module access, billing, commission and settlement.</p></div>
      </div>
      {applicablePlans.length ? (
        <>
          <label className="plan-assignment-select">Subscription plan <b>*</b>
            <select value={value.plan_id || ''} onChange={(event) => selectPlan(event.target.value)} required={!editMode || !value.plan_id}>
              <option value="">Select an active plan</option>
              {applicablePlans.map((plan) => <option key={plan.id} value={plan.id}>{plan.plan_name} ({plan.plan_code})</option>)}
            </select>
          </label>
          {selected && <div className="plan-assignment-summary">
            <div><small>Commercial model</small><strong>{String(selected.commercial_model || '').replaceAll('_', ' ')}</strong></div>
            <div><small>Billing cycle</small><strong>{selected.billing_cycle || 'Monthly'}</strong></div>
            <div><small>{selected.commercial_model === 'commission_only' ? 'Subscription fee' : 'Plan price'}</small><strong>{selected.commercial_model === 'commission_only' ? 'No subscription fee' : `${selected.currency || 'INR'} ${Number(selected.base_price || 0).toLocaleString()}`}</strong></div>
            <div><small>Commission</small><strong>{selected.commercial_model === 'subscription_only' ? 'No commission' : `${Number(selected.commission_rate || 0)}${selected.commission_type === 'percentage' ? '%' : ` ${selected.currency || 'INR'}`}`}</strong></div>
            <div><small>Validity</small><strong>{formatDate(value.billing_start_date)} to {formatDate(value.billing_end_date)}</strong></div>
            <div className="plan-assignment-confirm"><CheckCircle2 size={16} /> Terms copied from plan master</div>
          </div>}
        </>
      ) : <div className="plan-assignment-empty">No active pricing plan includes this CMS module. Create the plan in Subscription Plans &amp; Pricing first.</div>}
    </section>
  );
}

